import { z } from 'zod';
import { organizationRefSchema } from './organizations';
import { resourceKeySchema, timestampSchema, versionedSchema } from './common';
import { assetHref, assetKindSchema } from './assets';

/**
 * The document register: which revision of a drawing is the current one.
 *
 * A project produces drawings, specifications, method statements, inspection
 * certificates and reports, and each of them is reissued -- rev A, rev B, rev C.
 * The question the register exists to answer is not "where is the file", which a
 * shared folder answers badly but answers; it is **"which one is current, who
 * issued it, and when"**, which a folder cannot answer at all, because the
 * answer is a relationship between files rather than a property of one.
 *
 * ## Two records
 *
 * A **document** is the thing that keeps its identity across reissues: the
 * drawing `RAI-DR-001`, whatever revision it is on. A **revision** is one
 * issue of it, with its own file, date and issuer. Somebody quoting
 * "RAI-DR-001 rev B" is naming both.
 *
 * ## Exactly one revision is issued
 *
 * That is the invariant the whole feature turns on, and it is enforced by a
 * partial unique index rather than by application code -- the same arrangement
 * schedule baselines use for their one active baseline. Issuing rev B
 * supersedes rev A in the same transaction: a register that let two revisions
 * both claim to be current would be worse than no register, because somebody
 * would build to the wrong one.
 *
 * A superseded revision is never deleted. It is the record of what was issued
 * when, which is exactly what gets asked for afterwards.
 *
 * ## The discipline is free text with suggestions
 *
 * Deliberately not a configured catalog like the infrastructure asset types.
 * The values are few and stable ("Track", "Civil", "Signalling"), the register
 * suggests what the project has already used, and a second catalog table --
 * with its own service, its own settings section and its own translations --
 * would be a copy of an existing one for a field nobody administers. The cost
 * ledger's `documentGroupRef` already established this shape.
 */

/* ------------------------------------------------------------ where it is */

/**
 * The stored file behind a revision, resolved for the reader.
 *
 * The write side names an asset by id and this is the read side of the same
 * fact, which is a deliberate asymmetry rather than an oversight: an id cannot
 * label a control. A link reading `/api/v1/assets/8f3c...` is a link nobody
 * clicks; the same link reading `GA-plan-revB.pdf, 2.4 MB` is the thing the
 * reader opened the register to find.
 *
 * Resolved on the list as well as the detail, on the current revision only.
 * The register's job is to say which revision is current, and the row that
 * answers that is the row somebody should be able to open it from.
 */
export const documentFileSchema = z.object({
  assetId: z.string().uuid(),
  kind: assetKindSchema,
  /** Client-supplied at upload, sanitized there. The link's text. */
  filename: z.string().nullable(),
  contentType: z.string(),
  byteSize: z.number().int().nonnegative(),
});
export type DocumentFile = z.infer<typeof documentFileSchema>;

/**
 * An external location, for the projects where this register is an index into
 * somebody else's store.
 *
 * Absolute `http(s)` only. A relative path would resolve against this
 * deployment and quietly produce a link to a page that does not exist here, and
 * every other scheme -- `javascript:` first among them -- is an attack surface
 * on a value one person types and everybody else clicks.
 */
export const externalDocumentUrlSchema = z
  .string()
  .max(2000)
  .refine((value) => /^https?:\/\/\S+$/i.test(value), {
    message: 'must be an absolute http(s) URL',
  });

/* ------------------------------------------------------------- revisions */

/**
 * Where one issue of a document stands.
 *
 * `draft` is being prepared and is not to be built from. `issued` is the
 * current one, and there is at most one per document. `superseded` is what a
 * later issue replaced, kept because "what did rev A say" is a question with
 * consequences.
 *
 * Deliberately no `approved` or `for_review`: approval workflows differ between
 * every client and every contract form, and modelling one here would be
 * recording a process that happens in somebody else's system.
 */
export const DOCUMENT_REVISION_STATUSES = ['draft', 'issued', 'superseded'] as const;
export const documentRevisionStatusSchema = z.enum(DOCUMENT_REVISION_STATUSES);
export type DocumentRevisionStatus = (typeof DOCUMENT_REVISION_STATUSES)[number];

export const documentRevisionSchema = versionedSchema.extend({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  /**
   * What the revision is called on the drawing: `A`, `02`, `P01`. Free text
   * because every client numbers differently, and unique within its document so
   * "rev B" names one thing.
   */
  label: z.string(),
  status: documentRevisionStatusSchema,
  /** The day it was issued, which is not the day the row was written. */
  issuedOn: z.string().date().nullable(),
  /**
   * Who issued it, as free text. Frequently an external designer with no login
   * here, and a nullable user reference would quietly record "nobody".
   */
  issuedBy: z.string().nullable(),
  /**
   * The issuer from the organization register, when it is in it
   * (docs/organizations-design.md section 9.1). `issuedBy` keeps the name as
   * it was at the time; this is the organization, as it is now.
   */
  issuedByOrganization: organizationRefSchema.nullable(),
  notes: z.string().nullable(),
  /** The file, when one was attached. A revision with no file is a record that
   *  it exists -- which is still worth having, and is how a register gets
   *  populated before anybody has hunted down the PDFs. */
  assetId: z.string().uuid().nullable(),
  /** The same fact, resolved: `assetId` is what a save sends and this is what a
   *  link can be drawn from. Null exactly when `assetId` is. */
  file: documentFileSchema.nullable(),
  /** Where it lives when it lives somewhere else -- the client's Aconex,
   *  ProjectWise or SharePoint. Never set at the same time as `assetId`; see
   *  `checkRevisionSource`. */
  externalUrl: z.string().nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type DocumentRevision = z.infer<typeof documentRevisionSchema>;

/**
 * A revision is in one place, or in no place yet, and never in two.
 *
 * "Where is rev B" is a question with one answer. A revision carrying both an
 * upload and an external link offers a reader two Open controls that can lead
 * to two different files, which is the exact failure the whole register exists
 * to prevent: somebody builds to the wrong drawing.
 *
 * Shared by create and update so the two cannot drift apart, and mirrored by a
 * database `CHECK` -- the same belt-and-braces pairing the cost ledger's
 * `checkDocumentFields` uses, and for the same reason: a rule only the API
 * knows is a rule an import or a future endpoint can break silently.
 *
 * The update case is the subtle one. A `PATCH` carries only what it means to
 * change, so setting one source while the other is *already stored* has to be
 * refused too -- which the service does, because only it can see the stored
 * row. What this catches is the single request that sends both.
 */
function checkRevisionSource(
  data: { assetId?: string | null; externalUrl?: string | null },
  ctx: z.RefinementCtx,
): void {
  if ((data.assetId ?? null) !== null && (data.externalUrl ?? null) !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['externalUrl'],
      message: 'a revision has an uploaded file or an external link, not both',
    });
  }
}

/**
 * What may still be changed about a revision, given where it stands.
 *
 * The register's value is that "which revision is current, and what did the
 * last one say" has one answer that nobody can quietly rewrite. Deleting a
 * superseded revision was already refused for exactly that reason; editing one
 * was not, which left the same hole with a smaller door -- somebody could swap
 * the file behind rev B a year after rev C replaced it, and the register would
 * show a history that never happened.
 *
 * Three postures, and the middle one is the one that needed thinking about:
 *
 *  - **A draft** is somebody's working copy. Everything is editable, including
 *    deleting it outright.
 *  - **The issued revision** is what everybody is building from *now*, and
 *    corrections to it are ordinary: the wrong PDF was attached, the
 *    transmittal date was mistyped, the issuer's name was spelled wrong. It
 *    stays fully editable. What it may not become is dateless -- the database
 *    refuses that (`document_revision_issued_has_date`) and so does
 *    `checkRevisionDate` below, so the refusal names a field.
 *  - **A superseded revision** is the historical record, and the record is
 *    closed. Only `notes` may still be written, because notes are commentary
 *    rather than the record: "superseded by C, see RFI 214" is exactly what
 *    somebody needs to add afterwards, and it changes nothing about what was
 *    issued.
 *
 * Stated here, in the contract, rather than in the service, because the API and
 * the editor have to agree about it: a rule the server enforces and the form
 * does not know is a form that offers controls whose saves fail.
 */
export type RevisionEditability = 'full' | 'notes-only';

export function revisionEditability(status: DocumentRevisionStatus): RevisionEditability {
  return status === 'superseded' ? 'notes-only' : 'full';
}

/** The fields a `notes-only` revision still accepts. */
export const REVISION_NOTES_ONLY_FIELDS = ['notes'] as const;

/**
 * A revision that claims to be issued has to say when.
 *
 * The database states it too (`document_revision_issued_has_date`), and this is
 * the half that can name the field: a `PATCH` clearing `issuedOn` on a revision
 * that is already issued is legal on the wire and illegal in the row, and only
 * a caller holding the stored status can see the combination. Without this the
 * violation surfaces as an unreadable constraint error -- which is a 500, not a
 * correction anybody can act on.
 *
 * The mirror of `checkRevisionSource`, and split from it for the same reason
 * the source rule is: the single-request case belongs in the schema and the
 * stored-state case belongs where the stored state is.
 */
export function revisionDateWouldBeMissing(input: {
  status: DocumentRevisionStatus;
  storedIssuedOn: string | null;
  patchedIssuedOn?: string | null;
}): boolean {
  if (input.status === 'draft') return false;
  const next = input.patchedIssuedOn !== undefined ? input.patchedIssuedOn : input.storedIssuedOn;
  return next === null;
}

const revisionWritableFields = z.object({
  label: z.string().min(1).max(40),
  issuedOn: z.string().date().nullish(),
  issuedBy: z.string().max(200).nullish(),
  /** An organization from the register. When set, `issuedBy` is written with
   *  its name, whatever was sent -- the typed name is for issuers not in it. */
  issuedByPartyId: z.string().uuid().nullish(),
  notes: z.string().max(2000).nullish(),
  assetId: z.string().uuid().nullish(),
  externalUrl: externalDocumentUrlSchema.nullish(),
});

export const createDocumentRevisionSchema = revisionWritableFields
  .strict()
  .superRefine(checkRevisionSource);
export type CreateDocumentRevision = z.infer<typeof createDocumentRevisionSchema>;

/**
 * `status` is absent, and so it is on the update.
 *
 * Issuing is not a field edit: it supersedes whatever was current, in one
 * transaction, and has its own route. Rolling it into a general edit would let
 * a change of notes silently replace the drawing everybody is building from.
 */
export const updateDocumentRevisionSchema = revisionWritableFields
  .partial()
  .extend(versionedSchema.shape)
  .strict()
  .superRefine(checkRevisionSource);
export type UpdateDocumentRevision = z.infer<typeof updateDocumentRevisionSchema>;

/* ------------------------------------------------------------- documents */

export const documentSchema = versionedSchema.extend({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  key: resourceKeySchema,
  /** The number people quote: `RAI-DR-001`. Unique within the project. */
  reference: z.string(),
  title: z.string(),
  /** "Track", "Civil", "Signalling". Free text; the API suggests what the
   *  project already uses. */
  discipline: z.string().nullable(),
  notes: z.string().nullable(),

  /**
   * The revision that is current, flattened onto the document.
   *
   * A register is read as a list, and "which revision is current" is the column
   * every reader is scanning. Making them open each row to find out would be a
   * list that fails at its one job.
   */
  currentRevision: documentRevisionSchema.nullable(),
  /** How many issues exist, current and superseded. */
  revisionCount: z.number().int().nonnegative(),

  /**
   * Whether *this* caller follows it (docs/document-register-design.md
   * section 5).
   *
   * Per caller, never a count. Who else is watching a drawing is not a fact
   * the product publishes, and publishing it would turn following into a
   * visible act -- at which point people stop doing it on the documents where
   * it matters most.
   *
   * On the list rather than fetched per row, because the control that toggles
   * it is on the row: fifty documents would otherwise be fifty requests to
   * decide which way fifty buttons point.
   */
  following: z.boolean(),

  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Document = z.infer<typeof documentSchema>;

const documentWritableFields = z.object({
  reference: z.string().min(1).max(120),
  title: z.string().min(1).max(200),
  discipline: z.string().max(120).nullish(),
  notes: z.string().max(2000).nullish(),
});

export const createDocumentSchema = documentWritableFields
  .extend({ key: resourceKeySchema })
  .strict();
export type CreateDocument = z.infer<typeof createDocumentSchema>;

export const updateDocumentSchema = documentWritableFields
  .partial()
  .extend(versionedSchema.shape)
  .strict();
export type UpdateDocument = z.infer<typeof updateDocumentSchema>;

export const documentListQuerySchema = z.object({
  discipline: z.string().max(120).optional(),
  /** Documents with nothing issued yet: the register's own to-do list. */
  unissuedOnly: z.coerce.boolean().optional(),
  /**
   * Only the ones this caller follows.
   *
   * The filter is what makes a subscription something other than an email
   * setting: a project with four hundred drawings has perhaps eleven that any
   * one person is accountable for, and this is the view of those eleven.
   */
  followedOnly: z.coerce.boolean().optional(),
  search: z.string().max(120).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type DocumentListQuery = z.infer<typeof documentListQuerySchema>;

/** Distinct disciplines already in use, for the suggest control. */
export const disciplineSuggestQuerySchema = z.object({
  q: z.string().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(20).default(10),
});
export type DisciplineSuggestQuery = z.infer<typeof disciplineSuggestQuerySchema>;

/* --------------------------------------------------------------- reading */

/**
 * Whether a document has anything anybody may build from.
 *
 * Stated here rather than as `currentRevision !== null` at each call site,
 * because the list, the tile and the detail panel all ask it and a fourth
 * reader would eventually ask it differently.
 */
export function isIssued(document: Document): boolean {
  return document.currentRevision !== null;
}

/**
 * Where one revision can be opened, as one answer.
 *
 * Three call sites ask it -- the list row, the history row and the revision
 * editor -- and the exclusivity rule (`checkRevisionSource`) is only worth
 * having if the readers agree about which of the two fields wins. They agree
 * here, once, rather than each writing `file ? … : externalUrl ? … : …` and one
 * of them eventually writing it the other way round.
 *
 * `file` before `externalUrl` is not a preference: the database refuses rows
 * with both, so the order only decides what a row written before that
 * constraint existed would show, and an uploaded file is the one this
 * deployment can still serve.
 */
export type RevisionSource =
  | { kind: 'file'; href: string; filename: string | null; byteSize: number }
  | { kind: 'link'; href: string }
  | { kind: 'none' };

export function revisionSource(revision: DocumentRevision): RevisionSource {
  if (revision.file) {
    return {
      kind: 'file',
      href: assetHref(revision.file.assetId),
      filename: revision.file.filename,
      byteSize: revision.file.byteSize,
    };
  }
  if (revision.externalUrl) return { kind: 'link', href: revision.externalUrl };
  return { kind: 'none' };
}

/* --------------------------------------------------------- where it opens */

/**
 * The register's own route, and the query parameter that opens one row on it.
 *
 * Every reference to a document used to resolve to a bare `/documents`: the
 * link picker's `href`, the linked-items panel, and the email telling somebody
 * their drawing had been reissued. All three landed the reader on an unfiltered
 * list of four hundred rows and left them to find the one the link was about --
 * which is the work the link existed to save.
 *
 * A query parameter rather than a path segment (`/documents/:id`) because the
 * register is one screen: the row expands in place, the list stays visible, and
 * a reader who arrives from an email is looking at the drawing *in* its
 * register rather than at a page about it. A path would promise a detail screen
 * that does not exist.
 *
 * Stated here so the three callers cannot disagree, and so that the parameter's
 * name is written once -- the page reads it back with `DOCUMENT_OPEN_PARAM`.
 */
export const DOCUMENTS_PATH = '/documents';
export const DOCUMENT_OPEN_PARAM = 'open';

export function documentLinkPath(documentId: string): string {
  return `${DOCUMENTS_PATH}?${DOCUMENT_OPEN_PARAM}=${encodeURIComponent(documentId)}`;
}
